import { useEffect, useRef, useState } from "react";
import {
  useLocation,
  useMatch,
  useNavigate,
  useParams
} from "react-router-dom";

import {
  AGENTIC_AI_ROUTE,
  AGENTIC_AI_SEARCH_ROUTE
} from "@/components/agenticAI/constants";

const buildChatPath = (chatId?: string) =>
  chatId
    ? `${AGENTIC_AI_ROUTE}/${encodeURIComponent(chatId)}`
    : AGENTIC_AI_ROUTE;

/**
 * `resetWorkspace` empties the map for the conversation being left. It runs each
 * time the thread changes, whatever changed the URL: a chat handler, the app
 * sidebar, Back or Forward. A conversation swap is not a route change, so
 * `useAgenticWorkspaceSwap` never fires and the previous conversation's layers,
 * areas and results would stay painted under the new one.
 */
export const useAgenticAI = (resetWorkspace: () => void) => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const { chatId: pathChatId } = useParams();
  const isSearchOpen = !!useMatch(AGENTIC_AI_SEARCH_ROUTE);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMapFullScreen, setIsMapFullScreen] = useState(false);
  const [draftThreadId, setDraftThreadId] = useState(() => crypto.randomUUID());
  const [previousChatId, setPreviousChatId] = useState(pathChatId);

  const chatId = isSearchOpen ? previousChatId : pathChatId;

  if (previousChatId !== chatId) {
    setPreviousChatId(chatId);
    if (!chatId) setDraftThreadId(crypto.randomUUID());
  }

  const isAgentChatOpen = !!chatId;
  const agentThreadId = chatId ?? draftThreadId;

  // The first run is skipped: `useAgenticWorkspaceSwap` resets the page load,
  // before the SDK restores the thread.
  const previousThreadIdRef = useRef(agentThreadId);
  useEffect(() => {
    if (previousThreadIdRef.current === agentThreadId) return;
    previousThreadIdRef.current = agentThreadId;
    resetWorkspace();
  }, [agentThreadId, resetWorkspace]);

  const goToChat = async (nextChatId?: string) => {
    await navigate(`${buildChatPath(nextChatId)}${search}`, { replace: true });
  };

  const handleToggleSidebar = () => setIsSidebarOpen((isOpen) => !isOpen);

  const handleToggleMapFullScreen = () =>
    setIsMapFullScreen((isExpanded) => !isExpanded);

  const handleToggleSearch = async () => {
    if (isSearchOpen) {
      await goToChat(chatId);
      return;
    }
    setIsSidebarOpen(true);
    setIsMapFullScreen(false);
    await navigate(`${AGENTIC_AI_SEARCH_ROUTE}${search}`);
  };

  const handleNewChat = async () => {
    setIsMapFullScreen(false);
    setIsSidebarOpen(true);
    await goToChat();
  };

  const handleOpenAgentChat = async () => {
    setIsSidebarOpen(false);
    await goToChat(agentThreadId);
  };

  const handleSelectChat = async (selectedChatId: string) => {
    setIsMapFullScreen(false);
    await goToChat(selectedChatId);
  };

  return {
    activeChatId: chatId ?? null,
    agentThreadId,
    isAgentChatOpen,
    isMapFullScreen,
    isSearchOpen,
    isSidebarOpen,
    handleNewChat,
    handleOpenAgentChat,
    handleSelectChat,
    handleToggleMapFullScreen,
    handleToggleSearch,
    handleToggleSidebar
  };
};
